import React, { useState, useEffect } from 'react'
import { Box, Paper, Typography } from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { DateTime } from 'luxon'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { FilterExtraAction } from '../../components/_WMS/FilterComponent/types'
import { filterOptions } from './config/filterOptions'
import { KardexLegend } from './KardexLegend'

// Filtros específicos: acotan la búsqueda por sí mismos, por lo que exceptúan
// el límite de días del rango de fechas.
export const SPECIFIC_FILTER_KEYS = [
  'workOrderId',
  'asnId',
  'pickingRouteId',
  'sku',
  'ean13'
]

// Filtros obligatorios: al menos uno debe estar presente para poder buscar.
// El rango de fechas emite initialDateFilter / finalDateFilter.
export const REQUIRED_FILTER_KEYS = [
  ...SPECIFIC_FILTER_KEYS,
  'initialDateFilter',
  'finalDateFilter'
]

// Máximo de días permitido para el rango de fechas.
export const MAX_RANGE_DAYS = 30

// Devuelve la cantidad de días del rango de fechas seleccionado, o null si no hay rango.
export const getDateRangeDays = (filters: Filter[]): number | null => {
  const initial = filters.find((f) => f.key === 'initialDateFilter')?.value
  const final = filters.find((f) => f.key === 'finalDateFilter')?.value
  if (!initial || !final) return null
  return DateTime.fromISO(final).diff(DateTime.fromISO(initial), 'days').days
}

export const hasSpecificFilter = (filters: Filter[]): boolean =>
  filters.some((filter) => SPECIFIC_FILTER_KEYS.includes(filter.key))

// El rango está completo solo si están las dos fechas.
export const hasCompleteDateRange = (filters: Filter[]): boolean =>
  getDateRangeDays(filters) !== null

// El rango es válido si la búsqueda ya está acotada por un filtro específico
// (OT, ASN, Ruta, SKU, EAN13) o si no supera el máximo de días.
export const isDateRangeValid = (filters: Filter[]): boolean => {
  if (hasSpecificFilter(filters)) return true
  const days = getDateRangeDays(filters)
  return days === null || days <= MAX_RANGE_DAYS
}

// Sin un filtro específico, el rango de fechas debe venir completo: una fecha
// sola no acota la búsqueda por sí misma.
export const hasRequiredFilter = (filters: Filter[]): boolean =>
  hasSpecificFilter(filters) || hasCompleteDateRange(filters)

interface KardexSearchProps {
  onSearchClick: (filters: Filter[]) => void
  selectedFilters: Filter[]
  setSelectedFilters: React.Dispatch<React.SetStateAction<Filter[]>>
  extraActions?: FilterExtraAction[]
}

export const KardexSearch: React.FC<KardexSearchProps> = (props) => {
  const [showValidationAlert, setShowValidationAlert] = useState(true)
  const [showRangeError, setShowRangeError] = useState(false)

  const handleSearchWithValidation = (filters: Filter[]) => {
    if (!hasRequiredFilter(filters)) return
    if (!isDateRangeValid(filters)) {
      setShowRangeError(true)
      return
    }
    setShowRangeError(false)
    setShowValidationAlert(false)
    props.onSearchClick(filters)
  }

  // Mostrar/ocultar las alertas según los filtros seleccionados
  useEffect(() => {
    setShowValidationAlert(!hasRequiredFilter(props.selectedFilters))
    setShowRangeError(!isDateRangeValid(props.selectedFilters))
  }, [props.selectedFilters])

  return (
    <Box>
      <FilterComponent
        options={filterOptions}
        includeAddButton={true}
        onSearchClick={handleSearchWithValidation}
        selectedFilters={props.selectedFilters}
        setSelectedFilters={props.setSelectedFilters}
        requiredFilterKeys={REQUIRED_FILTER_KEYS}
        extraActions={props.extraActions}
        rightSlot={<KardexLegend />}
      />

      {showRangeError && (
        <Box mb={2}>
          <Paper
            elevation={1}
            style={{
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c2c7',
              borderRadius: '4px',
              padding: '12px 16px'
            }}
          >
            <Box display='flex' alignItems='center'>
              <ErrorOutlineIcon
                style={{ color: '#842029', marginRight: '8px', fontSize: '20px' }}
              />
              <Typography
                variant='body2'
                style={{ color: '#842029', fontWeight: 500 }}
              >
                El rango de fechas no puede superar los{' '}
                <strong>{MAX_RANGE_DAYS} días</strong>. Para buscar un período
                más amplio, agregue un filtro por <strong>OT</strong>,{' '}
                <strong>ID ASN</strong>, <strong>ID Ruta de Picking</strong>,{' '}
                <strong>SKU</strong> o <strong>EAN13</strong>.
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {showValidationAlert && !showRangeError && (
        <Box mb={2}>
          <Paper
            elevation={1}
            style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              padding: '12px 16px'
            }}
          >
            <Box display='flex' alignItems='center'>
              <WarningIcon
                style={{ color: '#856404', marginRight: '8px', fontSize: '20px' }}
              />
              <Typography
                variant='body2'
                style={{ color: '#856404', fontWeight: 500 }}
              >
                Para realizar la búsqueda, debe seleccionar al menos uno de los
                siguientes filtros: <strong>OT</strong>, <strong>ID ASN</strong>,{' '}
                <strong>ID Ruta de Picking</strong>, <strong>SKU</strong>,{' '}
                <strong>EAN13</strong>, o un <strong>Rango de Fechas</strong>{' '}
                completo (máx. {MAX_RANGE_DAYS} días si es el único filtro).
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  )
}
